/**
 * @file DecorCard.tsx
 * @description Карточка одного рисунка секции (phase19.16 Р2; решение
 * 2026-10-09 «рисунки у секции делаем»). SB120 этап 1 (uiux-sb120-stage1
 * §2.7; данные db-design-sb120-stage1 §1): место точкой на схеме
 * (`DecorPositionField`, x/y в %), размер числом 16–640 px с подсказками
 * S 96 · M 160 · L 256 и отдельно для телефона (нет ключа — «по доле»),
 * привязка к секции или колонке, «Скрыть на телефоне», «Выход за край».
 */

import type { SectionDecor } from "@chaiberi/shared";
import { X } from "lucide-react";

import { Button } from "../../../ui/button";
import { CheckRow } from "../../../site-view/CheckRow";
import { Segmented } from "../../../site-view/Segmented";
import { NumberSliderField } from "../../../site-fields/NumberSliderField";
import { DeviceValueRow } from "../fields/DeviceValueRow";
import { useSectionForm } from "../section-form-context";
import { DecorPositionField } from "./DecorPositionField";
import { contentMediaUrl } from "../../../../lib/api-content-media";
import { formatPx } from "../../../../lib/site-builder/look-space";

const SIZE_PRESETS = [
  { label: "S 96", value: 96 },
  { label: "M 160", value: 160 },
  { label: "L 256", value: 256 },
];

interface Props {
  decor: SectionDecor;
  index: number;
  columns: number;
  /** Доля телефона темы — для «по доле». */
  phoneFactor: number;
  onChange: (decor: SectionDecor) => void;
  onRemove: () => void;
}

export function DecorCard({ decor, index, columns, phoneFactor, onChange, onRemove }: Props) {
  const { preview } = useSectionForm();
  const attach = [{ value: "0", label: "К секции" }, ...Array.from({ length: columns }, (_, i) => ({ value: String(i + 1), label: `К колонке ${i + 1}` }))];
  const set = (patch: Partial<SectionDecor>) => onChange({ ...decor, ...patch });
  const base = `look.decor.${index}`;
  const phoneSize = decor.sizePhone ?? Math.round(decor.size * phoneFactor);

  return (
    <div className="flex flex-col gap-3 rounded-xl bg-surface-low p-3">
      <div className="flex items-center gap-3">
        <img src={contentMediaUrl(decor.mediaId)} alt="" className="size-14 rounded-lg bg-surface-lowest object-contain" />
        <span className="type-body flex-1 text-on-surface">Рисунок {index + 1}</span>
        <Button variant="ghost" size="icon" aria-label={`Убрать рисунок ${index + 1}`} className="size-12" onClick={onRemove}>
          <X aria-hidden />
        </Button>
      </div>
      {columns > 1 ? (
        <Segmented aria-label="Привязка рисунка" options={attach} value={String(decor.column ?? 0)} onChange={(v) => set({ column: v === "0" ? null : Number(v) })} />
      ) : null}
      <DecorPositionField label="Где" value={{ x: decor.x, y: decor.y }} onChange={(p) => set(p)} onPreview={(axis, v) => preview(`${base}.${axis}`, v)} />
      <NumberSliderField
        label="Размер"
        value={decor.size}
        inherit={{ value: 160, label: "M 160" }}
        min={16}
        max={640}
        step={1}
        largeStep={16}
        unit="px"
        display={{ factor: 1, decimals: 2 }}
        presets={SIZE_PRESETS}
        onChange={(size) => set({ size: size ?? 160 })}
        onPreview={(v) => preview(`${base}.size`, v ?? 160)}
      />
      {/* Поле телефона — всегда (QA D11): галочка «Скрыть» может быть снята позже. */}
      <DeviceValueRow summary={`на телефоне ${formatPx(phoneSize)} px${decor.sizePhone === undefined ? " (по доле)" : ""}${decor.hideOnPhone ? " · скрыт на телефоне" : ""}`} own={decor.sizePhone !== undefined} label="Размер на телефоне">
        <NumberSliderField
          label="Размер на телефоне"
          value={decor.sizePhone}
          inherit={{ value: Math.round(decor.size * phoneFactor), label: "по доле" }}
          min={16}
          max={640}
          step={1}
          unit="px"
          display={{ factor: 1, decimals: 2 }}
          onChange={(sizePhone) => {
            const next = { ...decor, sizePhone };
            if (sizePhone === undefined) delete next.sizePhone;
            onChange(next);
          }}
          onPreview={(v) => preview(`${base}.sizePhone`, v)}
        />
      </DeviceValueRow>
      <CheckRow checked={decor.hideOnPhone} onChange={(hideOnPhone) => set({ hideOnPhone })}>
        Скрыть на телефоне
      </CheckRow>
      <CheckRow checked={decor.bleed} onChange={(bleed) => set({ bleed })}>
        Выходит за край секции
      </CheckRow>
    </div>
  );
}
